import React, { useMemo, useState } from 'react';
import { ENDPOINTS } from 'features/daily-plan/constants';
import useDatatable, {
  addMultipleColumns,
  buildMultiplePhonesActionsRenderer,
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { rowClickBuilder } from 'hooks/profileActions';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import DataTable from 'UI/components/organisms/DataTable';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { RowActions } from 'UI/constants/defaults';
import { getFinalColumns } from 'UI/utils';
import { DataTableLink } from 'UI/utils/renderers';

import { formatResponse } from '../../DailyPlan.utils';
import { usePhoneActions } from '../../hooks/usePhoneActions';
import { ARCHIVED_ACTIONS } from '../ArchiveActions.constants';
import { ArchiveActionsButton } from '../ArchiveActionsButton';
import { ArchiveActionsDialog } from '../ArchiveActionsDialog';

import {
  ARCHIVED_ACTIONS_COLUMNS,
  ContactRoutes,
  ORDER_BY_OPTIONS,
  TABLE_KEY
} from './ArchivedActionsDrawer.constants';
import { useDrawerStyles } from './ArchivedActionsDrawer.styled';

const EXTRA_RENDERERS = {
  contactLink: value => {
    if (!value) return null;

    const { id, entityTypeId, fullName } = value;
    const route = ContactRoutes[entityTypeId];

    return id && route ? (
      <DataTableLink id={id} text={fullName} route={route} enableInTabRedirect />
    ) : null;
  }
};

export const ArchivedActionsDrawer = ({ onClose, onRestoreComplete, extendParams }) => {
  const classes = useDrawerStyles();

  const [uiState, setUiState] = useState({
    restoreDialogOpen: false,
    actionId: null
  });

  const { trackCommunicationAction, getContactInfo } = usePhoneActions({
    columns: ARCHIVED_ACTIONS_COLUMNS
  });

  const multiplePhonesActionButtons = buildMultiplePhonesActionsRenderer({
    customName: 'phoneNumbers',
    handleAction: trackCommunicationAction,
    columns: getFinalColumns(ARCHIVED_ACTIONS_COLUMNS),
    getContactInfo
  });

  const archivedActionsColumns = addMultipleColumns(getFinalColumns(ARCHIVED_ACTIONS_COLUMNS), [
    { column: multiplePhonesActionButtons, afterColumn: 'contactStatus' }
  ]).filter(column => !!column);

  const initialPreferences = getColumnPreferences(
    TABLE_KEY,
    0,
    ORDER_BY_OPTIONS,
    archivedActionsColumns
  );

  const {
    columnPreferences,
    count,
    data,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState,
    getData
  } = useDatatable({
    columnsDefinitions: archivedActionsColumns,
    endpoint: `${ENDPOINTS.tasks}?onlyArchived=true`,
    formatResponse,
    initialPreferences,
    key: TABLE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    shouldScrollOnNavigate: true,
    sendDateWithTimezone: true,
    paramsExtender: extendParams
  });

  const handleRestoreSingleTask = id => {
    setUiState(prev => ({ ...prev, actionId: id }));
  };

  const handleOpenRestoreActionsDialog = () => {
    setUiState(prev => ({ ...prev, restoreDialogOpen: true }));
  };

  const actions = buildRowActionsRendererDefinition({
    actions: [
      {
        name: RowActions.Restore,
        onClick: rowClickBuilder({
          columns: archivedActionsColumns,
          data,
          pickId: true,
          callback: itemId => handleRestoreSingleTask(itemId)
        }),
        enableWhen: () => !totalSelected
      }
    ]
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const {
    filteredItems,
    handleRowSelection,
    multiSelectComponents,
    selectedIndexesInPage,
    setToInitial,
    totalSelected
  } = useMultipleSelection({ data, count, perPage, disableSelectAllCheckbox: true });

  const refreshTable = async () => {
    setUiState(prev => ({ ...prev, restoreDialogOpen: false, actionId: null }));
    setToInitial();
    await getData();
    await onRestoreComplete();
  };

  const finalColumns = getColumnsToRender([...columns, actions], EXTRA_RENDERERS);

  const itemsToRestore = useMemo(
    () => (totalSelected > 0 ? filteredItems.data.map(item => item.id) : [uiState.actionId]),
    [filteredItems.data, totalSelected, uiState.actionId]
  );

  return (
    <FPDrawer
      CardHeaderProps={{ title: strings.dailyPlan.archiveActions.drawer.title }}
      FPDrawerProps={{
        open: true,
        onClose,
        classes: { paper: classes.drawerLarge }
      }}
      onClose={onClose}
      hasPaddingContent={false}
    >
      <DataTable
        columnOrder={columnOrder?.length ? columnOrder : undefined}
        columns={finalColumns}
        components={multiSelectComponents}
        count={count ?? 0}
        CustomToolbar={
          <CustomTableToolbarSelect total={totalSelected}>
            <ArchiveActionsButton
              mode={ARCHIVED_ACTIONS.Restore}
              onClick={handleOpenRestoreActionsDialog}
            />
          </CustomTableToolbarSelect>
        }
        data={data}
        draggableColumns={{
          enabled: true
        }}
        elevation={0}
        filter={false}
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onRowSelectionChange={handleRowSelection}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        rowsSelected={selectedIndexesInPage}
        searchText={keyword}
        selectableRows="multiple"
        selectToolbarPlacement="none"
        sortOrder={{ name: orderBy, direction }}
        subtitle={strings.dailyPlan.archiveActions.drawer.table.subtitle}
        title={strings.dailyPlan.archiveActions.drawer.table.title}
      />

      <When condition={!!uiState.restoreDialogOpen || !!uiState.actionId}>
        <ArchiveActionsDialog
          isLoading={uiState.isLoading}
          mode={ARCHIVED_ACTIONS.Restore}
          onComplete={refreshTable}
          onClose={() =>
            setUiState(prev => ({ ...prev, restoreDialogOpen: false, actionId: null }))
          }
          selectedItems={itemsToRestore}
        />
      </When>
    </FPDrawer>
  );
};
